import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Copy, Check, Globe, ExternalLink, Play, ChevronRight, Shield } from "lucide-react";
import { useState } from "react";
import { useChatbotConfig } from "../../hooks/useChatbotConfig";
import { getWidgetScriptUrl, generateIframeEmbedCode } from "../../config/apiConfig";

export function CMSPlatformsTab() {
  const { config } = useChatbotConfig();
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [expandedPlatform, setExpandedPlatform] = useState<string>('wordpress');

  const isConfigured = Boolean(config.businessId && config.isEnabled);
  const scriptUrl = isConfigured && config.businessId ? getWidgetScriptUrl(config.businessId) : 'SAVE_CONFIG_FIRST';
  const iframeCode = isConfigured && config.businessId ? generateIframeEmbedCode(config.businessId) : '<!-- Save your configuration first -->';

  const handleCopyCode = async (code: string, platform: string) => {
    await navigator.clipboard.writeText(code);
    setCopiedCode(platform);
    
    const toast = document.createElement('div');
    toast.textContent = `${platform} code copied!`;
    toast.style.cssText = 'position:fixed;top:20px;right:20px;background:#10B981;color:white;padding:12px 24px;border-radius:8px;z-index:9999;box-shadow:0 4px 12px rgba(0,0,0,0.15)';
    document.body.appendChild(toast);
    setTimeout(() => {
      document.body.removeChild(toast);
      setCopiedCode(null);
    }, 2000);
  };

  const platforms = [
    {
      id: 'wordpress',
      name: 'WordPress',
      icon: '🌐',
      popularity: '43% of websites',
      difficulty: 'Easy',
      time: '2 minutes',
      description: 'Most popular CMS with multiple integration methods',
      methods: [
        {
          title: 'Method 1: Custom HTML Block (Recommended)',
          steps: [
            'Edit your page/post in WordPress',
            'Add a "Custom HTML" block where you want the chatbot',
            'Paste the iframe code below',
            'Publish/Update your page'
          ],
          code: iframeCode
        },
        {
          title: 'Method 2: Theme Editor (Advanced)',
          steps: [
            'Login to your WordPress admin dashboard',
            'Go to Appearance → Theme Editor',
            'Select your active theme',
            'Open footer.php file',
            'Find the closing </body> tag',
            'Paste the JavaScript code just before </body>',
            'Click "Update File"'
          ],
          code: `<!-- Add this to footer.php before </body> -->
<script src="${scriptUrl}" async></script>`
        }
      ],
      tips: [
        'Always backup your site before editing theme files',
        'Use child theme to prevent losing changes during updates',
        'Plugin method is safer for beginners',
        'Test on staging site first'
      ]
    },
    {
      id: 'googlesites',
      name: 'Google Sites',
      icon: '🟦',
      popularity: '100M+ sites',
      difficulty: 'Easy',
      time: '2 minutes',
      description: 'Google\'s free website builder with embed support',
      methods: [
        {
          title: 'iframe Embed Integration',
          steps: [
            'Open your Google Site in edit mode at sites.google.com',
            'Click "Insert" from the right panel',
            'Select the "Embed" option',
            'Choose the "Embed Code" tab',
            'Paste the iframe code below into the text box',
            'Click "Next" to preview the chatbot',
            'Click "Insert" to add the chatbot to your page'
          ],
          code: iframeCode
        }
      ],
      tips: [
        'Widget will appear on the specific page where you embed it',
        'To add to all pages, embed on each page individually',
        'Google Sites may restrict some scripts for security',
        'Test the widget after publishing your site'
      ]
    },
    {
      id: 'wix',
      name: 'Wix',
      icon: '🎨',
      popularity: '200M+ users',
      difficulty: 'Medium',
      time: '4 minutes',
      description: 'Drag-and-drop builder with custom code support',
      methods: [
        {
          title: 'Method 1: iframe Embed (Recommended)',
          steps: [
            'Open your Wix Editor',
            'Click "Add" → "Embed" → "Custom Embeds"',
            'Select "iframe"',
            'Paste the iframe code below',
            'Adjust the size as needed',
            'Position the widget where you want it',
            'Publish your site'
          ],
          code: iframeCode
        },
        {
          title: 'Method 2: Custom Code (Advanced)',
          steps: [
            'Open your Wix Editor',
            'Click Settings in the top menu',
            'Select Custom Code from Advanced section',
            'Click + Add Custom Code',
            'Paste the JavaScript code below',
            'Set "Load code on" to "All pages"',
            'Choose "Body - end" for placement',
            'Click "Apply"'
          ],
          code: `<script src="${scriptUrl}" async></script>`
        }
      ],
      tips: [
        'Wix automatically optimizes script loading',
        'Widget appears on all published pages',
        'Preview before publishing changes',
        'Compatible with Wix mobile sites'
      ]
    },
    {
      id: 'blogger',
      name: 'Blogger',
      icon: '🟠',
      popularity: '400M+ blogs',
      difficulty: 'Easy',
      time: '3 minutes',
      description: 'Google\'s blogging platform with HTML widget support',
      methods: [
        {
          title: 'HTML/JavaScript Gadget',
          steps: [
            'Go to your Blogger dashboard',
            'Click "Layout" in the left sidebar',
            'Click "Add a Gadget" where you want the chatbot',
            'Select "HTML/JavaScript" from the list',
            'Paste the chatbot code in the content box',
            'Optionally add a title like "Customer Support"',
            'Click "Save" and then "Save arrangement"'
          ],
          code: `<script src="${scriptUrl}" async></script>`
        }
      ],
      tips: [
        'Place the gadget in sidebar or footer for site-wide availability',
        'Test the widget in both desktop and mobile view',
        'Blogger automatically handles script loading',
        'Widget will be visible on all blog pages'
      ]
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center p-6 rounded-xl bg-gradient-to-br from-purple-50 to-emerald-50 border border-purple-200/60">
        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-emerald-600 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-purple-500/25">
          <Globe className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-2xl font-bold text-slate-900 mb-2">CMS Platform Integration</h2>
        <p className="text-slate-600 max-w-2xl mx-auto mb-4">
          Step-by-step visual guides for popular content management systems and website builders.
        </p>
        
        {isConfigured ? (
          <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 px-4 py-2">
            <Check className="w-4 h-4 mr-2" />
            Ready to Deploy
          </Badge>
        ) : (
          <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 px-4 py-2">
            Configuration Required
          </Badge>
        )}
      </div>

      {/* Platform Selection */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {platforms.map((platform) => (
          <Card 
            key={platform.id}
            className={`cursor-pointer transition-all duration-200 hover:shadow-lg ${
              expandedPlatform === platform.id 
                ? 'ring-2 ring-blue-500 shadow-lg' 
                : 'border-slate-200'
            }`}
            onClick={() => setExpandedPlatform(platform.id)}
          >
            <CardContent className="p-4 text-center">
              <div className="text-3xl mb-2">{platform.icon}</div>
              <h3 className="font-semibold text-slate-900">{platform.name}</h3>
              <p className="text-xs text-slate-600 mt-1">{platform.popularity}</p>
              <div className="flex justify-center gap-2 mt-2">
                <Badge variant="outline" className="text-xs px-2 py-1">
                  {platform.difficulty}
                </Badge>
                <Badge variant="outline" className="text-xs px-2 py-1">
                  {platform.time}
                </Badge>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Detailed Integration Guide */}
      {platforms.map((platform) => 
        expandedPlatform === platform.id && (
          <Card key={platform.id} className="border border-slate-200/60 shadow-lg">
            <CardHeader className="pb-4 border-b border-slate-100/80">
              <CardTitle className="flex items-center gap-3">
                <span className="text-2xl">{platform.icon}</span>
                <div>
                  <div className="text-xl font-semibold text-slate-900">{platform.name} Integration</div>
                  <div className="text-sm text-slate-600 font-normal">{platform.description}</div>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6 space-y-6">
              {/* Methods */}
              {platform.methods.map((method, methodIndex) => (
                <div key={methodIndex} className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="text-lg font-semibold text-slate-900">{method.title}</h4>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleCopyCode(method.code, `${platform.name}-${methodIndex}`)}
                      className="shadow-sm"
                    >
                      {copiedCode === `${platform.name}-${methodIndex}` ? (
                        <>
                          <Check className="w-4 h-4 mr-2 text-green-600" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4 mr-2" />
                          Copy Code
                        </>
                      )}
                    </Button>
                  </div>

                  {/* Step-by-step Instructions */}
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-3">
                      <h5 className="font-medium text-slate-900 flex items-center gap-2">
                        <Play className="w-4 h-4 text-blue-600" />
                        Step-by-step Instructions:
                      </h5>
                      <ol className="space-y-2">
                        {method.steps.map((step, stepIndex) => (
                          <li key={stepIndex} className="flex items-start gap-3 text-sm">
                            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold flex items-center justify-center">
                              {stepIndex + 1}
                            </span>
                            <span className="text-slate-700">{step}</span>
                          </li>
                        ))}
                      </ol>
                    </div>

                    <div className="space-y-3">
                      <h5 className="font-medium text-slate-900">Code to Add:</h5>
                      <pre className="text-xs bg-slate-900 text-slate-100 p-4 rounded-lg overflow-x-auto">
                        <code className="text-green-400">{method.code}</code>
                      </pre>
                    </div>
                  </div>
                </div>
              ))}

              {/* Tips */}
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h5 className="font-medium text-blue-900 mb-2">💡 Platform-Specific Tips:</h5>
                <ul className="text-sm text-blue-800 space-y-1">
                  {platform.tips.map((tip, tipIndex) => (
                    <li key={tipIndex}>• {tip}</li>
                  ))}
                </ul>
              </div>

              {/* Quick Test Button */}
              <div className="flex justify-center pt-4">
                <Button 
                  className="bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 shadow-lg shadow-emerald-600/25"
                  onClick={() => window.open(`/chatbot-test.html?businessId=${config.businessId}`, '_blank')}
                  disabled={!isConfigured}
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Test Your Integration
                </Button>
              </div>
            </CardContent>
          </Card>
        )
      )}

      {/* Configuration Warning */}
      {!isConfigured && (
        <Alert className="border-amber-200 bg-amber-50">
          <AlertDescription className="text-amber-800">
            <strong>Configuration Required:</strong> Please save your chatbot configuration first to get the correct integration code.
          </AlertDescription>
        </Alert>
      )}

      {/* Domain Whitelisting Notice */}
      <Alert className="border-purple-200 bg-purple-50">
        <Shield className="h-4 w-4 text-purple-600" />
        <AlertDescription className="text-purple-800">
          <strong>🔒 Domain Authorization Required:</strong> Before your chatbot works on your website, you must contact Wildy support to add your domain to the authorized CUSTOMER_DOMAINS list. This security feature ensures only approved websites can use your chatbot. <a href="#" className="underline font-medium">Learn more in Troubleshooting</a>.
        </AlertDescription>
      </Alert>

      {/* Universal Benefits */}
      <div className="p-6 rounded-lg border border-emerald-200/60 bg-emerald-50/50">
        <h3 className="text-lg font-semibold text-slate-900 mb-4">🎯 Why Choose Our Widget</h3>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <h4 className="font-medium text-emerald-900">🚀 Universal Compatibility</h4>
            <p className="text-sm text-emerald-800">Works with all major CMS platforms and website builders without conflicts.</p>
          </div>
          <div className="space-y-2">
            <h4 className="font-medium text-emerald-900">⚡ Auto-Configuration</h4>
            <p className="text-sm text-emerald-800">No manual setup needed - loads your saved settings automatically.</p>
          </div>
          <div className="space-y-2">
            <h4 className="font-medium text-emerald-900">🔄 Always Updated</h4>
            <p className="text-sm text-emerald-800">Changes in your dashboard apply instantly to your website.</p>
          </div>
        </div>
      </div>
    </div>
  );
} 